import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1B285E',
      light: '#2c3d8f',
      dark: '#121b42',
      contrastText: '#fff',
    },
    secondary: {
      main: '#FDBD10',
      light: '#ffcd3c',
      dark: '#c79100',
      contrastText: '#000',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#1B285E',
      secondary: '#666666',
    }
  },
  typography: {
    fontFamily: '"Trasandina Web", Trasandina, sans-serif',
    allVariants: {
      fontFamily: '"Trasandina Web", Trasandina, sans-serif',
    },
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      color: '#1B285E',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: '#1B285E',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#1B285E',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      color: '#1B285E',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
      color: '#1B285E',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
      color: '#1B285E',
    },
    body1: {
      fontSize: '1rem',
      color: '#666666',
    },
    body2: {
      fontSize: '0.875rem',
      color: '#666666',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '1rem',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#666666',
    },
    overline: {
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      fontWeight: 600,
      letterSpacing: '0.08em',
      color: '#1B285E',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '*': {
            fontFamily: '"Trasandina Web", Trasandina, sans-serif',
          },
          body: {
            fontFamily: '"Trasandina Web", Trasandina, sans-serif',
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '50px',
          padding: '10px 30px',
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
        containedPrimary: {
          backgroundColor: '#1B285E',
          '&:hover': {
            backgroundColor: '#121b42',
          },
        },
        containedSecondary: {
          backgroundColor: '#FDBD10',
          color: '#1B285E',
          '&:hover': {
            backgroundColor: '#ffcd3c',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
        outlinedPrimary: {
          borderColor: '#1B285E',
          color: '#1B285E',
          '&:hover': {
            borderColor: '#121b42',
            backgroundColor: 'rgba(27, 40, 94, 0.04)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1B285E',
          color: '#ffffff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1B285E',
          color: '#ffffff',
          width: 280,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          backgroundColor: '#ffffff',
          padding: '24px',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
              borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
              borderColor: '#1B285E',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1B285E',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"Trasandina Web", Trasandina, sans-serif',
          },
          '& .MuiInputBase-input': {
            fontFamily: '"Trasandina Web", Trasandina, sans-serif',
          }
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
        colorSecondary: {
          color: '#FDBD10',
          '&:hover': {
            backgroundColor: 'rgba(253, 189, 16, 0.04)',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(253, 189, 16, 0.08)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#FDBD10',
          minWidth: '40px',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        },
        primary: {
          color: '#ffffff',
          fontSize: '1rem',
          fontWeight: 500,
        },
        secondary: {
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '0.875rem',
        }
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        },
        head: {
          fontWeight: 600,
          color: '#1B285E',
          backgroundColor: 'rgba(27, 40, 94, 0.04)',
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        }
      }
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.12)',
        },
      },
    },
  },
});

export default theme; 